import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api.js";
import { Badge, Button, Card, COLORS, Empty, Field, Form, Page } from "../ui.js";

interface Offer {
  id: string;
  title: string;
  priceCents: number;
  currency: string;
}

interface SalesPage {
  id: string;
  slug: string;
  mode: "TEMPLATE" | "BUILDER" | "CODE";
  isPublished: boolean;
  offer?: { id: string; title: string; priceCents: number; currency: string };
}

interface Template {
  key: string;
  name: string;
  tagline: string;
  blockCount: number;
}

export default function SalesPages() {
  const qc = useQueryClient();
  const nav = useNavigate();

  const list = useQuery({
    queryKey: ["sales-pages"],
    queryFn: () => api.get<{ items: SalesPage[] }>("/sales-pages"),
  });
  const offers = useQuery({
    queryKey: ["offers"],
    queryFn: () => api.get<{ items: Offer[] }>("/offers"),
  });
  const templates = useQuery({
    queryKey: ["sales-templates"],
    queryFn: () => api.get<{ items: Template[] }>("/sales-pages/templates"),
  });

  const [creating, setCreating] = useState(false);
  const [offerId, setOfferId] = useState("");
  const [templateKey, setTemplateKey] = useState<string>("");

  const create = useMutation({
    mutationFn: () =>
      api.post<SalesPage>("/sales-pages", {
        offerId,
        mode: "BUILDER",
        ...(templateKey ? { templateKey } : {}),
      }),
    onSuccess: (sp) => {
      qc.invalidateQueries({ queryKey: ["sales-pages"] });
      nav(`/sales-pages/${sp.id}/edit`);
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!offerId) return;
    create.mutate();
  }

  return (
    <Page title="Pages de vente">
      <p style={{ color: COLORS.muted }}>
        Une page de vente publie une Offre vers le grand public. Éditeur visuel par blocs.
      </p>

      {!creating ? (
        <Button onClick={() => setCreating(true)}>+ Nouvelle Page</Button>
      ) : (
        <Card>
          <Form onSubmit={onSubmit}>
            <Field label="Quelle Offre vendre ?">
              <select
                value={offerId}
                onChange={(e) => setOfferId(e.target.value)}
                required
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 6,
                  fontSize: 14,
                  fontFamily: "inherit",
                }}
              >
                <option value="">— Choisir —</option>
                {offers.data?.items.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.title} — {(o.priceCents / 100).toFixed(2)} {o.currency}
                  </option>
                ))}
              </select>
              {offers.data?.items.length === 0 && (
                <p style={{ color: COLORS.muted, fontSize: 13, marginTop: 8 }}>
                  Aucune Offre. <Link to="/offers">Créez-en une d'abord</Link>.
                </p>
              )}
            </Field>

            <Field label="Point de départ">
              <div style={{ display: "grid", gap: 10 }}>
                <TemplateCard
                  active={templateKey === ""}
                  onSelect={() => setTemplateKey("")}
                  name="Page vierge"
                  tagline="Démarrez sans rien et composez bloc par bloc."
                  blockCount={0}
                />
                {templates.data?.items.map((t) => (
                  <TemplateCard
                    key={t.key}
                    active={templateKey === t.key}
                    onSelect={() => setTemplateKey(t.key)}
                    name={t.name}
                    tagline={t.tagline}
                    blockCount={t.blockCount}
                  />
                ))}
              </div>
            </Field>

            <div style={{ display: "flex", gap: 8 }}>
              <Button type="submit" disabled={!offerId || create.isPending}>
                {create.isPending ? "Création…" : "Créer et éditer"}
              </Button>
              <Button variant="ghost" onClick={() => setCreating(false)}>
                Annuler
              </Button>
            </div>
          </Form>
        </Card>
      )}

      <div style={{ marginTop: 24 }}>
        {list.data?.items.length === 0 && <Empty>Aucune page. Créez la première.</Empty>}
        {list.data?.items.map((sp) => (
          <Card key={sp.id}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
              <div>
                <Link
                  to={`/sales-pages/${sp.id}/edit`}
                  style={{ fontSize: 18, fontWeight: 600, color: COLORS.text, textDecoration: "none" }}
                >
                  {sp.offer?.title ?? "Sans titre"}
                </Link>
                <div style={{ color: COLORS.muted, fontSize: 13, marginTop: 6, fontFamily: "monospace" }}>
                  /{sp.slug}
                </div>
                <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                  <Badge kind={sp.isPublished ? "ok" : "neutral"}>
                    {sp.isPublished ? "Publiée" : "Brouillon"}
                  </Badge>
                  <Badge kind="neutral">Mode : {sp.mode}</Badge>
                </div>
              </div>
              <div style={{ textAlign: "right", fontSize: 14 }}>
                {sp.offer && (
                  <div style={{ fontWeight: 600 }}>
                    {(sp.offer.priceCents / 100).toFixed(2)} {sp.offer.currency}
                  </div>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </Page>
  );
}

function TemplateCard({
  active,
  onSelect,
  name,
  tagline,
  blockCount,
}: {
  active: boolean;
  onSelect: () => void;
  name: string;
  tagline: string;
  blockCount: number;
}) {
  return (
    <label
      style={{
        border: `2px solid ${active ? COLORS.accent : COLORS.border}`,
        borderRadius: 10,
        padding: 14,
        cursor: "pointer",
        display: "block",
        background: active ? "#fdf6ee" : "#fff",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <input
          type="radio"
          name="template"
          checked={active}
          onChange={onSelect}
          style={{ margin: 0 }}
        />
        <strong>{name}</strong>
        {blockCount > 0 && (
          <span style={{ color: COLORS.muted, fontSize: 12 }}>· {blockCount} blocs</span>
        )}
      </div>
      <div style={{ color: COLORS.muted, fontSize: 13, marginTop: 6, marginLeft: 24 }}>
        {tagline}
      </div>
    </label>
  );
}
